import { format, type Market, type MarketAccount } from '@talos/kyoko';

import type { Balance } from '@src/types';
import { shouldShowDestinationBalance } from './types';

/** Nested map of currency → market account ID → balance, used across transfer UIs. */
export type BalancesByCurrencyMarketAccountID = Map<string, Map<number, Balance>>;

// Helper function to format labels exactly like the Addresses page
export function getTransferOptionLabel(marketAccount: MarketAccount, market?: Market): string {
  const marketDisplayName = market?.DisplayName || market?.Name || marketAccount.Market;
  const marketAccountDisplayName = marketAccount.DisplayName || marketAccount.Name;

  return `${marketDisplayName} - ${marketAccountDisplayName}`;
}

export function getAccountAvailableBalance(
  marketAccountId: number,
  aliasCurrency: string | undefined,
  balancesByCurrencyMarketAccountID: BalancesByCurrencyMarketAccountID | undefined
): { availableAmount: string; currency: string } | undefined {
  if (!aliasCurrency || balancesByCurrencyMarketAccountID == null) {
    return undefined;
  }

  const balance = balancesByCurrencyMarketAccountID.get(aliasCurrency)?.get(marketAccountId);

  return {
    availableAmount: balance?.AvailableAmount ?? '0',
    currency: balance?.Currency ?? aliasCurrency,
  };
}

// Formats the available balance with the currency increment.
export function getTransferBalanceDescription(
  marketAccountId: number,
  aliasCurrency: string | undefined,
  balancesByCurrencyMarketAccountID: BalancesByCurrencyMarketAccountID | undefined,
  defaultIncrement: string | undefined,
  destinationMarketAccount?: MarketAccount
): string | undefined {
  if (destinationMarketAccount != null && !shouldShowDestinationBalance(destinationMarketAccount)) {
    return undefined;
  }

  const availableBalance = getAccountAvailableBalance(
    marketAccountId,
    aliasCurrency,
    balancesByCurrencyMarketAccountID
  );

  if (!availableBalance) {
    return undefined;
  }

  return `${format(availableBalance.availableAmount, { spec: defaultIncrement, truncate: true })} ${
    availableBalance.currency
  }`;
}

export function formatSourceDestinationDescription(hashcode?: string, balanceText?: string): string | undefined {
  if (hashcode && balanceText) {
    return `${hashcode} • ${balanceText}`;
  }

  return hashcode ?? balanceText;
}
